/**
 * /admin/accounts/:id — tenant detail page.
 *
 * Sections:
 *   - Stats tiles (customers, bookings, last activity, active memberships)
 *   - Lifecycle controls (transition status with reason capture)
 *   - Owner contact info
 *   - Impersonate button (with strong warning)
 *   - Recent activity log (last 100 rows)
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { type AdminAccountDetail, type AdminActivityLogEntry, adminApi } from '../admin_api';

const STATUS_TRANSITIONS: Record<string, string[]> = {
  trial: ['active', 'past_due', 'pending_delete'],
  active: ['past_due', 'pending_delete'],
  past_due: ['active', 'grace'],
  grace: ['active', 'suspended'],
  suspended: ['active', 'pending_delete'],
  pending_delete: ['active', 'deleted'],
  deleted: [],
};

export function AdminAccountDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminAccountDetail | null>(null);
  const [log, setLog] = useState<AdminActivityLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Lifecycle transition form
  const [toStatus, setToStatus] = useState('');
  const [reason, setReason] = useState('');
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [transitionSuccess, setTransitionSuccess] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState(false);

  // Tier change
  const [tierSelect, setTierSelect] = useState('');
  const [tierSaving, setTierSaving] = useState(false);
  const [tierError, setTierError] = useState<string | null>(null);
  const [tierSuccess, setTierSuccess] = useState<string | null>(null);

  // Trial extension
  const [trialDays, setTrialDays] = useState('');
  const [trialExtending, setTrialExtending] = useState(false);
  const [trialError, setTrialError] = useState<string | null>(null);
  const [trialSuccess, setTrialSuccess] = useState<string | null>(null);

  // Delete tenant
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteSlugInput, setDeleteSlugInput] = useState('');
  const [deleteDeleting, setDeleteDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Impersonation
  const [impersonateToken, setImpersonateToken] = useState<string | null>(null);
  const [impersonateError, setImpersonateError] = useState<string | null>(null);
  const [impersonating, setImpersonating] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([adminApi.getAccount(id), adminApi.getActivityLog(id)])
      .then(([detail, activity]) => {
        setData(detail);
        setTierSelect(detail.account.tier);
        setLog(activity.rows);
        setLoading(false);
      })
      .catch((e: Error) => {
        setError(e.message);
        setLoading(false);
      });
  }, [id]);

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (error || !data) return <p className="text-sm text-red-600">{error ?? 'Not found'}</p>;

  const { account, stats } = data;
  const allowedTransitions = STATUS_TRANSITIONS[account.status] ?? [];

  async function handleTransition() {
    if (!id || !toStatus || !reason.trim()) return;
    setTransitioning(true);
    setTransitionError(null);
    setTransitionSuccess(null);
    try {
      const result = await adminApi.transitionStatus(id, toStatus, reason.trim());
      if (result.transitioned) {
        setTransitionSuccess(`Status changed: ${result.from} → ${result.to}`);
        // Refresh
        const detail = await adminApi.getAccount(id);
        setData(detail);
        setToStatus('');
        setReason('');
      }
    } catch (e) {
      setTransitionError((e as Error).message);
    } finally {
      setTransitioning(false);
    }
  }

  async function handleTierSave() {
    if (!id || !tierSelect) return;
    setTierSaving(true);
    setTierError(null);
    setTierSuccess(null);
    try {
      const result = await adminApi.updateAccount(id, {
        tier: tierSelect as 'starter' | 'pro' | 'enterprise',
      });
      setData((prev) => (prev ? { ...prev, account: result.account } : prev));
      setTierSuccess(`Tier updated to ${result.account.tier}`);
    } catch (e) {
      setTierError((e as Error).message);
    } finally {
      setTierSaving(false);
    }
  }

  async function handleTrialExtend() {
    if (!id || !trialDays) return;
    const days = Number.parseInt(trialDays, 10);
    if (!Number.isFinite(days) || days <= 0) return;
    setTrialExtending(true);
    setTrialError(null);
    setTrialSuccess(null);
    try {
      await adminApi.updateAccount(id, { extendTrialDays: days });
      setTrialSuccess(`Trial extended by ${days} day${days !== 1 ? 's' : ''}`);
      setTrialDays('');
      const detail = await adminApi.getAccount(id);
      setData(detail);
    } catch (e) {
      setTrialError((e as Error).message);
    } finally {
      setTrialExtending(false);
    }
  }

  async function handleDelete() {
    if (!id || !data) return;
    setDeleteDeleting(true);
    setDeleteError(null);
    try {
      await adminApi.deleteAccount(id, deleteSlugInput.trim());
      navigate('/admin/accounts');
    } catch (e) {
      setDeleteError((e as Error).message);
      setDeleteDeleting(false);
    }
  }

  async function handleImpersonate() {
    if (!id) return;
    setImpersonating(true);
    setImpersonateError(null);
    try {
      const resp = await adminApi.impersonate(id);
      setImpersonateToken(resp.token);
    } catch (e) {
      setImpersonateError((e as Error).message);
    } finally {
      setImpersonating(false);
    }
  }

  return (
    <div className="max-w-4xl space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold">{account.name}</h1>
        <p className="text-sm text-muted-foreground num">
          {account.slug} · {account.id}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Customers', value: stats.customersCount },
          { label: 'Bookings', value: stats.bookingsCount },
          { label: 'Active memberships', value: stats.activeMembershipsCount },
          {
            label: 'Last booking',
            value: stats.lastBookingAt ? stats.lastBookingAt.slice(0, 10) : '—',
          },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white rounded-lg border border-[oklch(88%_0.05_70)] p-4">
            <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">{label}</p>
            <p className="text-lg font-semibold num">{value}</p>
          </div>
        ))}
      </div>

      {/* Account info + owner */}
      <div className="bg-white rounded-lg border border-[oklch(88%_0.05_70)] p-4 grid grid-cols-2 gap-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Status</p>
          <p className="font-medium">{account.status}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Tier</p>
          <p className="font-medium capitalize">{account.tier}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Currency</p>
          <p className="font-medium num">{account.currency}</p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Created</p>
          <p className="font-medium num">{account.createdAt.slice(0, 10)}</p>
        </div>
        {stats.ownerName && (
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Owner</p>
            <p className="font-medium">{stats.ownerName}</p>
            {stats.ownerEmail && (
              <p className="text-sm text-muted-foreground">{stats.ownerEmail}</p>
            )}
          </div>
        )}
        {account.stripeAccountId && (
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
              Stripe Connect
            </p>
            <p className="text-sm num">{account.stripeAccountId}</p>
            <p className="text-xs text-muted-foreground">
              Charges: {account.stripeChargesEnabled ? 'enabled' : 'disabled'} · Payouts:{' '}
              {account.stripePayoutsEnabled ? 'enabled' : 'disabled'}
            </p>
          </div>
        )}
      </div>

      {/* Lifecycle transition */}
      {allowedTransitions.length > 0 && (
        <div className="bg-white rounded-lg border border-[oklch(88%_0.05_70)] p-4">
          <h2 className="text-base font-semibold mb-3">Lifecycle controls</h2>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground block mb-1">Transition to</label>
              <select
                value={toStatus}
                onChange={(e) => setToStatus(e.target.value)}
                className="w-full text-sm border border-gray-200 rounded px-2 py-1.5"
              >
                <option value="">Select status…</option>
                {allowedTransitions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground block mb-1">Reason (required)</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Payment failed after 3 retries"
                className="w-full text-sm border border-gray-200 rounded px-2 py-1.5"
              />
            </div>
            <button
              type="button"
              disabled={!toStatus || !reason.trim() || transitioning}
              onClick={handleTransition}
              className="px-4 py-1.5 bg-[#004AAD] text-white text-sm rounded disabled:opacity-40"
            >
              {transitioning ? 'Transitioning…' : 'Apply'}
            </button>
          </div>
          {transitionError && <p className="text-sm text-red-600 mt-2">{transitionError}</p>}
          {transitionSuccess && (
            <p className="text-sm text-emerald-600 mt-2">{transitionSuccess}</p>
          )}
        </div>
      )}

      {/* Change Tier */}
      <div className="bg-white rounded-lg border border-[oklch(88%_0.05_70)] p-4">
        <h2 className="text-base font-semibold mb-3">Change tier</h2>
        <div className="flex gap-3 items-end">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Tier</label>
            <select
              value={tierSelect}
              onChange={(e) => setTierSelect(e.target.value)}
              className="text-sm border border-gray-200 rounded px-2 py-1.5"
            >
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          <button
            type="button"
            disabled={tierSaving || tierSelect === account.tier}
            onClick={() => void handleTierSave()}
            className="px-4 py-1.5 bg-[#004AAD] text-white text-sm rounded disabled:opacity-40"
          >
            {tierSaving ? 'Saving…' : 'Save tier'}
          </button>
        </div>
        {tierError && <p className="text-sm text-red-600 mt-2">{tierError}</p>}
        {tierSuccess && <p className="text-sm text-emerald-600 mt-2">{tierSuccess}</p>}
      </div>

      {/* Extend Trial — only shown when account is in trial */}
      {account.status === 'trial' && (
        <div className="bg-white rounded-lg border border-[oklch(88%_0.05_70)] p-4">
          <h2 className="text-base font-semibold mb-3">Extend trial</h2>
          <div className="flex flex-wrap gap-2 mb-3">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setTrialDays(String(d))}
                className={`px-3 py-1 text-sm rounded border ${trialDays === String(d) ? 'bg-[#004AAD] text-white border-[#004AAD]' : 'border-gray-200 text-muted-foreground hover:border-[#004AAD]'}`}
              >
                +{d} days
              </button>
            ))}
          </div>
          <div className="flex gap-3 items-end">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Custom days</label>
              <input
                type="number"
                min={1}
                step={1}
                value={trialDays}
                onChange={(e) => setTrialDays(e.target.value)}
                placeholder="e.g. 21"
                className="text-sm border border-gray-200 rounded px-2 py-1.5 w-28 num"
              />
            </div>
            <button
              type="button"
              disabled={trialExtending || !trialDays || Number.parseInt(trialDays, 10) <= 0}
              onClick={() => void handleTrialExtend()}
              className="px-4 py-1.5 bg-[#004AAD] text-white text-sm rounded disabled:opacity-40"
            >
              {trialExtending ? 'Extending…' : 'Extend trial'}
            </button>
          </div>
          {trialError && <p className="text-sm text-red-600 mt-2">{trialError}</p>}
          {trialSuccess && <p className="text-sm text-emerald-600 mt-2">{trialSuccess}</p>}
        </div>
      )}

      {/* Delete Tenant */}
      <div className="bg-white rounded-lg border border-red-200 p-4">
        <h2 className="text-base font-semibold text-red-700 mb-1">Delete tenant</h2>
        <p className="text-sm text-muted-foreground mb-3">
          Marks this account as deleted. This action is audit-logged and irreversible via the admin
          panel.
        </p>
        {!showDeleteDialog ? (
          <button
            type="button"
            onClick={() => setShowDeleteDialog(true)}
            className="px-4 py-1.5 bg-red-600 text-white text-sm rounded"
          >
            Delete tenant…
          </button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              Type <code className="bg-gray-100 px-1 rounded num">{account.slug}</code> to confirm:
            </p>
            <div className="flex gap-3 items-end">
              <input
                type="text"
                value={deleteSlugInput}
                onChange={(e) => setDeleteSlugInput(e.target.value)}
                placeholder={account.slug}
                className="text-sm border border-gray-200 rounded px-2 py-1.5 flex-1"
              />
              <button
                type="button"
                disabled={deleteDeleting || deleteSlugInput.trim() !== account.slug}
                onClick={() => void handleDelete()}
                className="px-4 py-1.5 bg-red-600 text-white text-sm rounded disabled:opacity-40"
              >
                {deleteDeleting ? 'Deleting…' : 'Confirm delete'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteSlugInput('');
                  setDeleteError(null);
                }}
                className="px-4 py-1.5 text-sm border border-gray-200 rounded text-muted-foreground"
              >
                Cancel
              </button>
            </div>
            {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
          </div>
        )}
      </div>

      {/* Impersonation */}
      <div className="bg-white rounded-lg border border-amber-200 p-4">
        <h2 className="text-base font-semibold mb-1">Impersonate tenant</h2>
        <p className="text-sm text-muted-foreground mb-3">
          Generates a one-time 10-minute token. Every impersonation is audit-logged. The
          impersonated session is read-only by convention — no write actions should be taken while
          impersonating.
        </p>
        <button
          type="button"
          disabled={impersonating}
          onClick={handleImpersonate}
          className="px-4 py-1.5 bg-amber-600 text-white text-sm rounded disabled:opacity-40"
        >
          {impersonating ? 'Generating token…' : 'Generate impersonation token'}
        </button>
        {impersonateError && <p className="text-sm text-red-600 mt-2">{impersonateError}</p>}
        {impersonateToken && (
          <div className="mt-3 p-3 bg-amber-50 rounded text-sm">
            <p className="font-medium mb-1">Token (copy this now — it will not be shown again):</p>
            <code className="text-xs break-all select-all">{impersonateToken}</code>
          </div>
        )}
      </div>

      {/* Recent activity log */}
      <div className="bg-white rounded-lg border border-[oklch(88%_0.05_70)] p-4">
        <h2 className="text-base font-semibold mb-3">Recent activity (last 100)</h2>
        {log.length === 0 ? (
          <p className="text-sm text-muted-foreground">No activity recorded.</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {log.map((entry) => (
              <div key={entry.id} className="py-2 flex gap-3 text-sm">
                <span className="text-xs text-muted-foreground num shrink-0 w-36">
                  {entry.createdAt.slice(0, 19).replace('T', ' ')}
                </span>
                <span className="font-medium">{entry.action}</span>
                <span className="text-muted-foreground text-xs">
                  {entry.subjectType} {entry.subjectId.slice(0, 8)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
